import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark, faSave, faSpinner } from "@fortawesome/free-solid-svg-icons";
import * as Constants from "../../utils/Constants";
import * as UrlConstants from "../../utils/UrlConstants";
import { getClientIdOfUser, getAuthToken, showToast } from "../../utils/utility";
import LockShuttle from "./LockShuttle";

const TripDeviceAssignment = ({ trip, onClose }) => {
    const [allLocks, setAllLocks]       = useState([]);
    const [assignedIds, setAssignedIds] = useState([]);
    const [loading, setLoading]         = useState(true);
    const [saving, setSaving]           = useState(false);

    const clientId = getClientIdOfUser();
    const base     = `${Constants.BASE_URL}${Constants.API_V1}/${clientId}`;

    useEffect(() => {
        setLoading(true);
        Promise.all([
            fetch(`${base}/assets/${UrlConstants.GET_ALL_ASSETS}`, {
                headers: { Authorization: `${Constants.BEARER} ${getAuthToken()}` },
            }).then(r => r.json()),
            fetch(`${base}/${UrlConstants.TRIPS}?page=0&size=500`, {
                headers: { Authorization: `${Constants.BEARER} ${getAuthToken()}` },
            }).then(r => r.json()),
        ])
            .then(([assetsData, tripsData]) => {
                const gpsLocks = (assetsData.responseData || []).filter(
                    a => (a.assetType || "").toUpperCase() === "GPS_LOCK"
                );
                const allTrips = tripsData.responseData?.content || tripsData.responseData || [];
                const activeDeviceIds = new Set(
                    allTrips
                        .filter(t => (t.status === "ACTIVE" || t.status === "SCHEDULED") && t.id !== trip.id)
                        .flatMap(t => t.deviceIds || [])
                );
                setAllLocks(gpsLocks.filter(a => !activeDeviceIds.has(a.assetUnitId)));
                setAssignedIds(trip.deviceIds || []);
            })
            .catch(() => showToast("Failed to load devices", { type: "error" }))
            .finally(() => setLoading(false));
    }, []);

    const handleSave = () => {
        setSaving(true);
        fetch(`${base}/${UrlConstants.TRIPS}/${trip.id}/${UrlConstants.TRIP_DEVICES}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                Authorization: `${Constants.BEARER} ${getAuthToken()}`,
            },
            body: JSON.stringify({ requestBody: { deviceIds: assignedIds } }),
        })
            .then(res => {
                if (!res.ok) return res.json().then(d => { throw new Error(d.responseMessage || res.statusText); });
                return res.json();
            })
            .then(() => {
                showToast(`Saved — ${assignedIds.length} lock(s) assigned`, { type: "success" });
                onClose();
            })
            .catch(err => showToast(err.message || "Failed to save devices", { type: "error" }))
            .finally(() => setSaving(false));
    };

    return (
        <div
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div style={{ background: "#fff", borderRadius: "10px", width: "780px", maxWidth: "96vw", maxHeight: "88vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}>
                {/* Header */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #e0e0e0" }}>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: "16px" }}>Manage GPS Locks — Fleet Trip</div>
                        <div style={{ fontSize: "13px", color: "#666", marginTop: "2px" }}>{trip.name}</div>
                    </div>
                    <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "20px", color: "#666" }}>
                        <FontAwesomeIcon icon={faCircleXmark} />
                    </button>
                </div>

                {/* Body */}
                <div style={{ padding: "20px", overflowY: "auto", flex: 1 }}>
                    {/* Info note */}
                    <div style={{ borderLeft: "3px solid #f9a825", background: "#fffde7", padding: "10px 14px", borderRadius: "0 6px 6px 0", fontSize: "13px", color: "#333", marginBottom: "16px", lineHeight: "1.6" }}>
                        <strong>Note:</strong> Alerts (lock/unlock, tamper, battery) are sent only for locks in the <strong>Assigned</strong> list
                        and only while this fleet trip is <strong>Active</strong>.
                        Each GPS lock can belong to only one active or scheduled trip at a time —
                        locks already assigned to another trip are hidden from the available list.
                    </div>
                    <LockShuttle
                        allLocks={allLocks}
                        assigned={assignedIds}
                        onChange={setAssignedIds}
                        disabled={saving}
                        loading={loading}
                    />
                </div>

                {/* Footer */}
                <div style={{ padding: "12px 20px", borderTop: "1px solid #e0e0e0", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                    <button className="btn-sm btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
                    <button className="btn-sm btn-primary" onClick={handleSave} disabled={saving || loading} style={{ minWidth: "100px" }}>
                        {saving ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSave} />}
                        {" "}{saving ? "Saving…" : "Save Changes"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TripDeviceAssignment;
